import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { PanelBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import Signal, { s_subscribe } from "badmfck-signal";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { FormParser } from "../FormParser";

interface TTestPaymentPopupProps {
	onSuccess?: () => void;
	merchant: IClientGroup;
	sec: ISecData[];
	url: string;
}

interface ISecData {
	security_token: string;
	kid: string;
}

// interface IInitData {
// 	amount: number;
// 	currency: string;
// 	referenceID: string;
// 	returnUrl: string;
// 	product?: IProduct;
// 	customer: ICustomer;
// }

// interface ICustomer {
// 	name: string;
// 	email: string;
// 	countryCode: string;
// 	postalCode: string;
// 	addressLine1: string;
// 	addressLine2?: string;
// 	city: string;
// 	state: string;
// 	phone: string;
// }

// id 	string 	✓ 	product id
// title 	string 	✓ 	product title
// subtitle 	string 	✓ 	product subtitle
// imageUrl 	string 	✓ 	product image url
// interface IProduct {
// 	id?: string;
// 	title: string;
// 	subtitle: string;
// 	imageUrl: string;
// 	//server side param __optional
// 	__optional?: boolean;
// }

export const S_TEST_PAYMENT_POPUP = new Signal<TTestPaymentPopupProps | null>("S_TEST_PAYMENT_POPUP");

const GATEWAY_BASE = "https://gateway.newtech.lv/api/v1";

/** HMAC-SHA256 in the browser via WebCrypto. `keyHex` is the merchant's security_token as hex. */
async function hmacSHA256Hex(keyHex: string, message: string): Promise<string> {
	const bytes: number[] = [];
	for (let i = 0; i < keyHex.length; i += 2) bytes.push(parseInt(keyHex.slice(i, i + 2), 16));
	const keyBytes = new Uint8Array(bytes);
	const cryptoKey = await crypto.subtle.importKey("raw", keyBytes, { name: "HMAC", hash: { name: "SHA-256" } }, false, ["sign"]);
	const signature = await crypto.subtle.sign("HMAC", cryptoKey, new TextEncoder().encode(message));
	return [...new Uint8Array(signature)].map(b => b.toString(16).padStart(2, "0")).join("");
}

/** Builds the PayAuth authorization header exactly the way gate verifies it (see TestAp.ts). */
async function buildAuthHeader(sec: ISecData, dataBase64: string): Promise<string> {
	const ts = Date.now();
	const nonce = Math.random().toString(36).substring(2, 15);
	const sig = await hmacSHA256Hex(sec.security_token, dataBase64 + ts + nonce);
	return `PayAuth v=1, ts=${ts},nonce=${nonce},kid=${sec.kid},sig=${sig}, alg=hmac-sha256`;
}

/** Static preview used in the popup before any actual request is signed. */
const previewHeader = (sec: ISecData): string =>
	`PayAuth v=1, ts=<runtime>,nonce=<runtime>,kid=${sec.kid},sig=<computed from data + ts + nonce using security_token>, alg=hmac-sha256`;

s_subscribe(() => {
	
}, "formParser_test-payment-credentials");

export const TestPaymentPopup = () => {
	useLocale();
	const [state, _setState] = useState<"small" | "full">("full");
	const [_busy, _setBusy] = useState(false);
	const [_referenceId, setReferenceId] = useState(Math.random().toString());
	const request = S_TEST_PAYMENT_POPUP.use();
	// credentials are derived from `request` instead of mirrored into state — otherwise the
	// child FormParser's onRequest runs on first render BEFORE a useEffect could populate
	// state, so it would cache kid/sig as undefined. Reading the value straight from the
	// signal payload avoids that race entirely.
	const currentCredentials: ISecData | null = request?.sec?.[0] ?? null;

	// state below is only for results of submitting (signed header, response, error) —
	// reset whenever a new popup opens so we don't show stale data from a previous session
	const [signedHeader, setSignedHeader] = useState<string>();
	const [currentRequestData, setCurrentRequestData] = useState();
	const [currentPaymentLink, setCurrentPaymentLink] = useState<string>();
	const [currentRequestDataB64, setCurrentRequestDataB64] = useState<string>();
	const [currentError, setCurrentError] = useState<string>();

	useEffect(() => {
		setSignedHeader(undefined);
		setCurrentRequestData(undefined);
		setCurrentPaymentLink(undefined);
		setCurrentRequestDataB64(undefined);
		setCurrentError(undefined);
		setReferenceId(Math.random().toString());
	}, [request]);

	const currentHeader = signedHeader ?? (currentCredentials ? previewHeader(currentCredentials) : "");

	if (!request) return null;

	let sx: RecursiveCSS = {};
	if (state === "small") {
		sx = { maxWidth: "620px", minWidth: "420px", width: "99vw", minHeight: "95vh", maxHeight: "95vh" };
	} else {
		sx = { maxWidth: "920px", minWidth: "420px", width: "99vw", minHeight: "95vh", maxHeight: "95vh" };
	}

	sx = {
		...sx,
		transition: "all .2s",
	};

	const closePopup = () => {
		//setState("small");
		S_TEST_PAYMENT_POPUP.invoke(null);
	};

	const callPay = async (data: any): Promise<any> => {
		setCurrentRequestData(data);
		setReferenceId(data.referenceID);
		setCurrentError(undefined);
		setCurrentPaymentLink(undefined);

		const dataBase64 = btoa(JSON.stringify(data));
		setCurrentRequestDataB64(dataBase64);

		if (!currentCredentials) {
			setCurrentError(msg("noCredentialsSelected"));
			return { error: msg("noCredentialsSelected") };
		}

		// sign exactly as gate verifies: HMAC-SHA256(security_token, b64 + ts + nonce)
		const authHeader = await buildAuthHeader(currentCredentials, dataBase64);
		setSignedHeader(authHeader);

		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "",
			url: `${GATEWAY_BASE}/payment/init`,
			data: { b64: dataBase64 },
			headers: { Authorization: authHeader },
			excludeAuth: true,
		});

		if (res.error) {
			setCurrentError(res.error.message + (res.error.details ? ` — ${res.error.details}` : ""));
			return res;
		}

		const responseData: { ssid: number; url: string } = res.data;
		if (responseData?.url) setCurrentPaymentLink(responseData.url);
		return res;
	};

	const onResult = (_res?: any) => {};

	return (
		<Panel sx={sx} decorator="absolute" label={msg("testPayment_cf250a73") + request.merchant.name} onClose={closePopup}>
			<FormParser
				size="small"
				secondaryButton={null}
				primaryButton={null}
				previewMode
				onSubmit={function (_data: unknown): Promise<any> {
					throw new Error(msg("functionNotImplemented"));
				}}
				id="test-payment-credentials"
				onRequest={(): any => {
					return {
						credentials: {
							kid: currentCredentials?.kid,
							sig: currentCredentials?.security_token,
							payment_window: request.url,
						},
						$__credentials_decorator: {
							title: msg("paymentCredentials"),
							subtitle: msg("credentialsToAccessPaymentApi"),
							blocks: [
								{
									blockless: true,
									order: ["kid", "sig"],
									gridTemplateColumns: "max-content 1fr",
								},
								{
									blockless: true,
									order: ["payment_window"],
								},
							],
						},
					};
				}}
			/>
			<PanelBox>
				<Typo variant="medium" bold>{msg("authorizationHeader")}</Typo>
				<Typo variant="caption">{msg("exampleOfAuthorizationHeader")}</Typo>
				<Spacer variant="small" />
				<Pre wrap data={currentHeader} />
			</PanelBox>

			<PanelBox>
				<Typo variant="medium" bold>{msg("requestBody")}</Typo>
				<Typo variant="caption">{msg("exampleOfRequestBody")}</Typo>
				<Spacer variant="small" />
				<Pre wrap data={currentRequestDataB64} />
			</PanelBox>

			{currentError && (
				<PanelBox>
					<Typo variant="medium" bold sx={{ color: "#d9534f" }}>{msg("error")}</Typo>
					<Spacer variant="small" />
					<Pre wrap data={currentError} />
				</PanelBox>
			)}

			<Spacer size="20px" />

			<FormParser
				size="small"
				onSubmit={function (data: any): Promise<any> {
					return callPay(data);
				}}
				onChange={(data: any) => {
					// live preview as the user types — request body + JSON snapshot stay in sync
					setCurrentRequestData(data);
					setCurrentRequestDataB64(btoa(JSON.stringify(data)));
				}}
				onComplete={onResult}
				secondaryButton={null}
				primaryButton={{
					title: msg("createPaymentRequest"),
				}}
				onRequest={async (): Promise<any> => {
					const res = await GD.REQ_API.request({
						httpMethod: "GET",
						method: "",
						url: `${GATEWAY_BASE}/payment/form/get`,
						data: undefined,
						excludeAuth: true,
					});

					if (res.data) {
						// sensible defaults for a one-click test payment — operator can edit before submit
						res.data.amount = 1;
						res.data.currency = "EUR";
						res.data.referenceID =
							Date.now().toString().slice(5) +
							"_" +
							Math.floor(Math.random() * 100000)
								.toString()
								.padStart(5, "0");

						res.data.customer.name = "John Doe";
						res.data.customer.email = "test@test.com";
						res.data.customer.countryCode = "LV";
						res.data.customer.postalCode = "1001";
						res.data.customer.city = "Riga";
						res.data.customer.addressLine1 = "Test street 1";
						res.data.customer.addressLine2 = "Apartment 1";
						res.data.customer.state = "Riga municipality";
						res.data.customer.phone = "+37111111111";
						res.data.customer.ip = "10.11.12.13";

						res.data["$__customer_decorator"] = {
							title: msg("customerInfo"),
							subtitle: msg("customerInformationForPayment"),
							blocks: [
								{
									blockless: true,
									order: ["name", "phone", "email", "ip"],
									gridTemplateColumns: "1fr 1fr 1fr 1fr",
								},

								{
									blockless: true,
									order: ["countryCode", "postalCode", "city", "state"],
									gridTemplateColumns: "100px 100px 1fr 1fr",
								},
								{
									blockless: true,
									order: ["addressLine1", "addressLine2"],
									gridTemplateColumns: "1fr 1fr",
								},
							],
						};
					}

					return res;
				}}
			/>

			<VBox gap="12px" sx={{ width: "50%" }}>
				<Pre data={currentRequestData} wrap style={{ flex: 1 }} />
				<Button
					disabled={!currentPaymentLink}
					variant="secondarySmall"
					onClick={() => {
						window.open(currentPaymentLink, "_blank");
					}}
					title={msg("openPayWindow")}
				/>
			</VBox>
		</Panel>
	);
};

// function isObject(item: any): boolean {
// 	return item !== null && typeof item === "object" && !Array.isArray(item);
// }

// function deepMerge<T extends object>(target: T, ...sources: Array<Partial<T>>): T {
// 	if (!sources.length) return target;
// 	const source = sources.shift();

// 	if (isObject(target) && isObject(source)) {
// 		for (const key in source) {
// 			if (Object.prototype.hasOwnProperty.call(source, key)) {
// 				const sourceValue = source[key];
// 				if (isObject(sourceValue) && isObject(target[key])) {
// 					target[key] = deepMerge(target[key] as any, sourceValue as any);
// 				} else {
// 					(target as any)[key] = sourceValue;
// 				}
// 			}
// 		}
// 	}
// 	return deepMerge(target, ...sources);
// }
